import { computed, watch } from 'vue';
import { useField } from 'vee-validate';
import VarType from 'Js/enums/varType';
export function useValidation(
/**
 * Indicates whether the input is already touched
 */
isDirty, 
/**
 * Set of props
 */
props, 
/**
 * Model value
 */
modelValue) {
    const ruleList = computed(() => {
        if (!props.rules)
            return [];
        return Array.isArray(props.rules) ? props.rules : [props.rules];
    });
    const localErrorMessage = computed(() => {
        if (!isDirty.value)
            return '';
        for (const rule of ruleList.value) {
            const result = typeof rule === VarType.Function ? rule(modelValue.value) : rule;
            if (typeof result === VarType.String)
                return result;
        }
        return '';
    });
    const validationName = computed(() => props.name || props.id || props.staticId?.toString());
    const validator = (value) => {
        for (const rule of ruleList.value) {
            const result = typeof rule === VarType.Function ? rule(value) : rule;
            if (result !== true)
                return result;
        }
        return true;
    };
    const result = (() => {
        if (!validationName.value) {
            return {
                errorMessage: localErrorMessage,
                onChange: () => {
                },
                onBlur: () => {
                },
                isValid: computed(() => localErrorMessage.value === ''),
                validate: async () => {
                    isDirty.value = true;
                    return localErrorMessage.value === '';
                },
            };
        }
        const { errorMessage, setValue, setTouched, meta, validate } = useField(validationName, validator, {
            initialValue: modelValue.value,
        });
        const visibleErrorMessage = computed(() => {
            if (!meta.value?.touched && meta.value?.valid)
                return '';
            return errorMessage.value || '';
        });
        watch(() => meta.value?.touched, (touched) => {
            if (touched) {
                isDirty.value = true;
            }
        });
        return {
            errorMessage: visibleErrorMessage,
            onChange: (value) => {
                // `handleChange` is primarily for DOM events. Using it with raw values can
                // cause normalization side-effects (and in some scenarios trigger reactive
                // update loops). For our custom inputs we always pass the new value
                // directly, so `setValue` is the safest API.
                setValue(value);
            },
            onBlur: () => {
                setTouched(true);
            },
            isValid: computed(() => {
                return meta.value?.valid;
            }),
            validate: async () => {
                const result = await validate();
                isDirty.value = true;
                setTouched(true);
                return result.valid;
            },
        };
    })();
    watch(modelValue, (val) => {
        result.onChange(val);
    });
    return result;
}
